import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
  Fragment,
  useEffect,
} from "react"
import AcButton from "~/components/atoms/AcButton"
import AcModal from "~/components/atoms/AcModal"
import AcRadio from "~/components/atoms/AcRadio"
import { FormProvider, useForm } from "react-hook-form"
import styled from "styled-components"
import { SignDocument } from "~/model/Contract"
import { DocumentType } from "~/model/Workflow"
import { isTypeInvoiceWf5, isTypeShare, shortenDocumentPdfId } from "~/utils/functions"
import { useGlobal } from "./useGlobal"
import * as Sentry from "@sentry/browser"
import { Values } from "~/model/Config"
import { MessageType, UserType } from "~/components/template/video/Meet"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { selectTemporary } from "~/slices/temporarySlice"
import { useBlc } from "./useBlc"
import { DocWorkflowNos } from "~/model/ContractDocument"
import { VideoContext } from "~/components/twilio/VideoProvider"

interface IParticipant {
  userId: number
  userType: string
}
interface CallVideoState {
  selectId: number
  setSelectId: Dispatch<SetStateAction<number>>
  handleChangeTab: (id: number) => void
  setDocument: Dispatch<SetStateAction<SignDocument[]>>
  pdfUrl: string
  getPdfUrl: (pdfId: string) => Promise<string>
  setFile: (name: string, value: string) => void
  participantList: IParticipant[]
  setParticipantList: Dispatch<SetStateAction<IParticipant[]>>
}

interface Props {
  children: ReactNode
}

interface ChooseFile {
  ITFile: string | null
  contractorFile: string | null
}

export const DATA_KEY = {
  CHOOSE_FILE: "chooseFile",
  CHANGE_TAB: "changeTab",
  CHANGE_PAGE_NUMBER: "changePageNumber",
  SCROLL: "scroll",
  PDF_MUTATION: "pdfMutation",
  INPUT_VALUES: "inputValues",
  MODAL: "modal",
  MODE: "mode",
  SIGN_DOCUMENT: "signDocument",
  COLUMN_NAMES_REF: "columnNamesRef",
  IS_SIGN_ERROR: "isSignError",
  RECORD_LOADING: "recordLoading",
  RECORD: "record",
  PARTICIPANT: "participant",
  PARTICIPANTS: "participants",
  AUDIO_LEVEL: "audioLevel",
} as const
export type DataKey = typeof DATA_KEY[keyof typeof DATA_KEY]

const CallVideoContext = createContext<CallVideoState>({} as CallVideoState)

export const CallVideoProvider = ({ children }: Props) => {
  const { downloadPdf } = useBlc()
  const { loading, showDialog } = useGlobal()

  const [selectId, setSelectId] = useState(1)
  const [openSelectFileModal, setOpenSelectFileModal] = useState(false)
  const [ITFiles, setITFiles] = useState<Values[]>([])
  const [contractorFiles, setContractorFiles] = useState<Values[]>([])
  const [document, setDocument] = useState<SignDocument[]>([])
  const [pdfUrl, setPdfUrl] = useState("")
  const [participantList, setParticipantList] = useState<IParticipant[]>([])
  const { userType } = useSelector(selectTemporary)
  const { sendData, client } = useContext(VideoContext)

  const methods = useForm<ChooseFile>({
    defaultValues: {
      ITFile: "url",
    },
  })
  const { setValue, getValues } = methods

  const displayFile = (file: SignDocument) => ({
    label: (
      <div className="file_row_item">
        <span>{file.workflowName}</span>
        <span>{`${dayjs(file.updatedAt).format("YYYY/MM/DD HH:mm")} 送信`}</span>
        <span>{shortenDocumentPdfId(file.pdfId)}</span>
      </div>
    ),
    value: file.pdfId,
  })

  const getFileList = () => {
    const ITFileData: Values[] = []
    const contractorFileData: Values[] = []
    const ITDocs = []
    if (document.length) {
      document.forEach((item) => {
        if ([DocumentType.IMPORTANT, DocumentType.CONTRACT].includes(item.workflowType)) {
          // const file = displayFile(item)
          // ITFileData.push(file)
          ITDocs.push(item)
        }
        if (isTypeShare(item) || isTypeInvoiceWf5(item)) {
          const file = displayFile(item)
          contractorFileData.push(file)
        }

        if (item.docWorkflowNo === DocWorkflowNos.DOC_WORKFLOW_17) {
          const file = displayFile(item)
          contractorFileData.push(file)
        }
      })
      ITDocs.sort((a, b) => {
        if (a.workflowType === DocumentType.IMPORTANT && b.workflowType === DocumentType.CONTRACT) {
          return -1
        }
        if (a.workflowType === DocumentType.CONTRACT && b.workflowType === DocumentType.IMPORTANT) {
          return 1
        }
        return 0
      })

      ITDocs.forEach((item) => {
        const file = displayFile(item)
        ITFileData.push(file)
      })
      setITFiles(ITFileData)
      setContractorFiles(contractorFileData)
    }

    // set initial value
    if (ITFileData.length) {
      setValue("ITFile", ITFileData[0].value as string)
    } else if (contractorFileData.length) {
      setValue("contractorFile", contractorFileData[0].value as string)
    }
  }

  useEffect(() => {
    getFileList()
  }, [document])

  const getPdfUrl = async (pdfId: string) => {
    try {
      loading("ファイルを読み込み中です... 0%")
      const res = await downloadPdf(pdfId, (progress) => {
        loading(`ファイルを読み込み中です... ${progress.percent}%`)
      })
      const file = new Blob([res], { type: "application/pdf" })
      const fileURL = URL.createObjectURL(file)
      setPdfUrl(fileURL)
      return fileURL
    } catch (e) {
      showDialog("エラー", e.message, "戻る")
      Sentry.captureException(e)
    } finally {
      loading(false)
    }
  }

  const handleChangeTab = (id: number) => {
    if (id === 2 && userType === UserType.AGENT) {
      setOpenSelectFileModal(true)
    } else {
      setSelectId(id)
    }
  }

  const closeModal = () => {
    setOpenSelectFileModal(false)
  }

  const goToSharePdfTab = () => {
    sendData({
      type: MessageType.NOTIFY,
      data: {
        [DATA_KEY.CHANGE_TAB]: { tabId: 2 },
      },
    })
    setSelectId(2)
    closeModal()
  }

  const handleChooseFile = async () => {
    const { ITFile, contractorFile } = getValues()
    const currentUserId = client?.getCurrentUserInfo()?.userId
    if (ITFile) {
      await getPdfUrl(ITFile)
      sendData({
        type: MessageType.NOTIFY,
        data: {
          [DATA_KEY.CHOOSE_FILE]: {
            name: "ITFile",
            pdfId: ITFile,
            userIdHandle: currentUserId,
          },
        },
      })
    }
    if (contractorFile) {
      await getPdfUrl(contractorFile)
      sendData({
        type: MessageType.NOTIFY,
        data: {
          [DATA_KEY.CHOOSE_FILE]: {
            name: "contractorFile",
            pdfId: contractorFile,
            userIdHandle: currentUserId,
          },
        },
      })
    }
    goToSharePdfTab()
  }

  const setFile = (name: keyof ChooseFile, value: string) => {
    setValue(name, value)
    if (name === "ITFile") {
      setValue("contractorFile", null)
    }
    if (name === "contractorFile") {
      setValue("ITFile", null)
    }
  }

  return (
    <CallVideoContext.Provider
      value={{
        selectId,
        handleChangeTab,
        setDocument,
        pdfUrl,
        getPdfUrl,
        setFile,
        participantList,
        setParticipantList,
        setSelectId,
      }}
    >
      <FormProvider {...methods}>
        <AcModal
          size="xl"
          show={openSelectFileModal}
          onHide={closeModal}
          title={"画面共有（書類表示）"}
        >
          <Fragment key={"body"}>
            <ModalContent>
              <p className="text">ご契約者さまの画面に表示する書類を選択してください。</p>
              <div className="file_group">
                <p>IT重説関連書類</p>
                <div className="file_group_inner">
                  <AcRadio
                    name="ITFile"
                    dataSource={ITFiles}
                    onChange={(value: string) => setFile("ITFile", value)}
                  />
                </div>

                <p>お客様交付書類</p>
                <div className="file_group_inner">
                  <AcRadio
                    name="contractorFile"
                    dataSource={contractorFiles}
                    onChange={(value: string) => setFile("contractorFile", value)}
                  />
                </div>
              </div>
            </ModalContent>
          </Fragment>
          <Fragment key={"footer"}>
            <AcButton className="btn-common m-1" onClick={closeModal}>
              キャンセル
            </AcButton>
            <AcButton className="btn-conversion m-1" onClick={handleChooseFile}>
              選択した書類を表示する
            </AcButton>
          </Fragment>
        </AcModal>
      </FormProvider>
      {children}
    </CallVideoContext.Provider>
  )
}

export const useCallVideoState = () => {
  const context = useContext(CallVideoContext)
  if (!context) {
    throw new Error("useCallVideoState must be used within a useCallVideoProvider")
  }
  return context
}

const ModalContent = styled.div`
  color: black;
  padding: 0 40px;
  .text {
    margin: 20px 0 30px 0 !important;
  }
  .file_group {
    height: 330px;
    overflow: auto;
    background-color: #f5f5f5;
    padding: 35px;
  }
  .file_group_inner {
    margin: 15px 0 30px 0;
    border-top: 3px solid #a8a8a8;

    .form-check-inline {
      display: block;
      border-bottom: 2px solid #d1d1d1;
      padding: 15px 0 15px 10px;
      margin: 0;
    }

    .file_row_item {
      display: flex;
      > span {
        width: 33%;
      }
    }
  }

  @media only screen and (max-width: 990px) {
    padding: 0 10px;
    .file_group {
      padding: 15px;
    }
    .file_group_inner {
      margin: 15px;
      .file_row_item {
        span {
          width: 100%;
        }
        span:nth-child(n + 2) {
          display: none;
        }
      }
    }
  }
`
